import "./CustomRadioGroup.scss";

/**
 * Reusable Radio Group
 *
 * Props:
 * - id: string
 * - value: any (selected option value)
 * - onChange: (nextValue) => void
 * - options: [{ value, label, disabled? }]
 * - label: string (group label)
 * - labelPosition: "start" | "end" (align label container)
 * - labelWidth: css width (e.g. "150px", "max-content")
 * - labelStyle: inline style for label text
 * - disabled: boolean (disables entire group)
 * - border: "Y" | "N" (wrap border same style as ToggleSwitch)
 * - direction: "row" | "column"
 * - size: "sm" | "md" | "lg"
 * - delete_yn: boolean (show clear icon)
 * - onClickClear: (id) => void
 * - normalizeValue: (v) => v (optional)
 */
export const CustomRadioGroup = ({
    id,
    value,
    onChange,
    options = [],
    label,
    labelPosition = "start",
    labelWidth = "150px",
    labelStyle = {},
    disabled = false,
    border = "N",
    direction = "row",
    size = "md",
    delete_yn = false,
    onClickClear,
    normalizeValue,
}) => {
    const currentValue = normalizeValue ? normalizeValue(value) : value;

    const handleSelect = (next) => {
        if (disabled) return;
        onChange?.(next);
    };

    const handleKeyDown = (e, optValue, optDisabled) => {
        if (disabled || optDisabled) return;
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            e.stopPropagation();
            handleSelect(optValue);
        }
    };

    const handleClearClick = (e) => {
        e.stopPropagation();
        if (onClickClear) onClickClear(id);
    };

    const anySelected = currentValue !== undefined && currentValue !== null && currentValue !== "";

    return (
        <div
            className={[
                "custom-radio-group",
                border === "Y" ? "border" : "",
                disabled ? "disabled" : "",
                `dir-${direction}`,
                `size-${size}`,
            ].join(" ")}
            role="radiogroup"
            aria-disabled={disabled}
        >
            {labelPosition === "start" && (
                <div className="labels" style={{ width: labelWidth }}>
                    <div className="label" style={labelStyle}>
                        {label}
                    </div>
                </div>
            )}

            <div className="radio-options">
                {options.map((opt, idx) => {
                    const isChecked = currentValue === opt.value;
                    const isOptDisabled = disabled || !!opt.disabled;

                    return (
                        <div
                            key={`${id}-${idx}`}
                            className={[
                                "radio-option",
                                isChecked ? "checked" : "",
                                isOptDisabled ? "opt-disabled" : "",
                            ].join(" ")}
                            role="radio"
                            aria-checked={isChecked}
                            aria-disabled={isOptDisabled}
                            tabIndex={isOptDisabled ? -1 : 0}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (!isOptDisabled) handleSelect(opt.value);
                            }}
                            onKeyDown={(e) => handleKeyDown(e, opt.value, isOptDisabled)}
                        >
                            <span className="dot" aria-hidden="true" />
                            <span className="text">{opt.label}</span>
                        </div>
                    );
                })}
            </div>

            {/* {labelPosition === "end" && (
                <div className="labels" style={{ width: labelWidth }}>
                    <div className="label" style={labelStyle}>
                        {label}
                    </div>
                </div>
            )} */}

            {delete_yn && anySelected && (
                <button
                    type="button"
                    className="clear-btn"
                    onClick={handleClearClick}
                    aria-label="Clear selection"
                >
                    ×
                </button>
            )}
        </div>
    );
};
